import { selectFeatureFlags } from 'core/userContext';
import { AudienceList } from 'modules/domains/campaign';
import { UseQueryResult } from 'react-query';
import { useSelector } from 'react-redux';
import { ApiError } from 'services/api';
import { RestQuery, useRestQueries } from 'utils/hooks/api';
import { useCampaignIdParam } from './useCampaignIdParam';

type QueryListSizeResponseData = {
  size?: number;
};

type QueryListSizeRequest = {
  listId: number;
};

export type QueryListsSizesReturnValue = QueryListSizeResponseData &
  Pick<
    UseQueryResult<QueryListSizeResponseData | undefined, ApiError>,
    'refetch' | 'error' | 'isLoading'
  >;

const QUERY_LIST_SIZE_API_URL = '/i/lists/getSize';

export const useQueryListsSizes = (
  lists: AudienceList[],
): ReadonlyArray<QueryListsSizesReturnValue> => {
  const campaignId = useCampaignIdParam();
  const canFetchListSize = !useSelector(selectFeatureFlags).DisableListsSizeQueryFetch;

  const queries: RestQuery<QueryListSizeResponseData, QueryListSizeRequest>[] = lists.map(
    ({ id: listId }) => ({
      url: QUERY_LIST_SIZE_API_URL,
      params: { listId },
      queryOptions: {
        enabled: canFetchListSize,
        /**
         * Note: we are using the campaign id as a key here as we want to refetch the lists data when changing between campaigns.
         */
        queryKey: [QUERY_LIST_SIZE_API_URL, campaignId, listId],
        refetchOnWindowFocus: false,
        staleTime: 1000 * 60 * 5, // 5 minutes
        cacheTime: 1000 * 60 * 5, // 5 minutes
      },
    }),
  );

  const responses = useRestQueries<QueryListSizeResponseData, QueryListSizeRequest>(queries);

  return responses?.map(({ data, error, isLoading, refetch }) => ({
    size: data?.size,
    error,
    isLoading,
    refetch,
  }));
};
